/**
 * THE WORDS OF THE PLATFORM CONSOLE.
 *
 * English is the ORIGIN and pt-BR the translation, same seed and shape as
 * `features/billing/i18n/*`. One audience reads all of it — whoever runs the
 * installation — and they read it in the panel, so every key follows the person.
 *
 * ── THE ONE SENTENCE HERE THAT DOES NOT ───────────────────────────────────
 *
 * `support.reasonPlaceholder`. What gets TYPED into that box is read by the
 * workspace being looked at, on the band `SupportSessionBanner` draws, and that
 * workspace has its own language. So the placeholder is translated for whoever
 * types and the reason itself is stored and shown verbatim — this product does
 * not paraphrase the sentence someone signed their name to.
 *
 * ── AND THE ONES THE SERVER OWNS ──────────────────────────────────────────
 *
 * An audit action (`tenant.status`, `support_session.grant`), an HTTP status, a
 * route, a model id, a tenant's slug, an error's stored message. Those arrive
 * written and are shown raw. `audit.*` translates the SENTENCE around the three
 * actions this screen knows how to narrate; a fourth action shows its own code,
 * which is how a newly recorded action appears on screen the day it starts being
 * recorded rather than the day someone remembers to add it here.
 */
export const platformMessages = {
	en: {
		/**
		 * Crédito na mão, do console — a rota existia e não tinha tela.
		 *
		 * `plan` zera na virada do ciclo e `bonus` sobrevive: dar no bolso errado é
		 * dar créditos que somem no fim do mês sem ninguém entender porquê.
		 */
		credits: {
			title: "Credits, by hand",
			balance: "plan {plan} · bonus {bonus}",
			blocked: "AI is blocked for this workspace",
			amount: "Credits",
			amountHint: "Negative takes back.",
			pool: "Pocket",
			poolBonus: "Bonus (survives the cycle)",
			poolPlan: "Plan (resets on close)",
			reason: "Why — at least ten characters",
			apply: "Apply",
			applied: "{n} credits applied. Balance is now {total}.",
			reasonShort:
				"The reason needs at least ten characters — it is what explains this line months from now.",
			blockTitle: "Manual stop",
			blockLead:
				"It survives every refill: topping credits up does not undo it. Only what is here lifts it.",
			blockReason: "Why block (or lift it) — at least ten characters",
			block: "Block AI",
			unblock: "Lift the block",
			blockedSince: "Blocked: {reason}",
			historyTitle: "What has been moved here",
			historyEmpty: "Nobody has moved this balance by hand yet.",
			historyNone: "no reason recorded",
			kindAdjustment: "adjustment",
			kindBlock: "block",
			kindUnblock: "unblock",
		},
		audit: {
			title: "Audit",
			lead: "Every platform action, stored in a table — it survives a restart.",
			empty: "Nothing has been done on this installation yet.",
			someone: "someone",
			aWorkspace: "a workspace",
			statusChange: "{who} moved {what} from {from} to {to}",
			grant: "{who} opened a support session on {what} for {minutes} min",
			revoke: "{who} revoked the support session on {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Presence",
			readyOf: "{ready} of {total} ready",
			ready: "ready",
			openConversations: "{n} open",
		},
		notice: {
			title: "Notices",
			new: "new notice",
			message: "Message",
			target: "target",
			allWorkspaces: "all workspaces",
			duration: "for",
			tone: "color",
			for1h: "1 hour",
			for1d: "1 day",
			for1w: "1 week",
			for30d: "30 days",
			publish: "publish",
			link: "Link (optional)",
			open: "open",
			empty: "No active notices.",
			createFailed: "Could not publish the notice.",
			revokeFailed: "Could not revoke the notice.",
			bannerUntil: "until {time}",
		},
		support: {
			bannerUntil: "Platform support can see this workspace until {time}.",
			bannerReason: "Reason: “{reason}”",
			bannerGrantedBy: " — granted by {name}",
			openTitle: "Open support sessions",
			endsIn: "“{reason}” — {who}, ends in {remaining}",
			expiring: "expiring",
			revoke: "Revoke",
			revokeFailed: "Could not revoke that session.",
			copyLink: "copy access link",
			linkCopied: "Access link copied — whoever holds it enters as admin until the deadline.",
			grantFailed: "Could not open the support session.",
			activeUntil: "Support session open until {until} — “{reason}”.",
			bandNote: "This workspace is seeing a band about it.",
			reason: "Reason — the workspace will read this sentence",
			reasonPlaceholder: "why someone needs to look at this workspace",
			forHowLong: "For",
			open: "Open the session",
		},
		usage: {
			tabs: {
				label: "Usage views",
				history: "History",
				panel: "Panel",
				workspaces: "By workspace",
			},
			title: "Usage",
			period: "Period",
			days: "{n} days",
			inPeriod: "{money} in the period",
			workspace: "workspace",
			day: "day",
			model: "model",
			turns: "turns",
			errors: "errors",
			tokens: "tokens",
			cost: "cost",
			models: "models",
			empty:
				"No turn in the period. If that looks wrong, it is a question for the log — the read runs under each workspace's own context, never outside it.",
			from: "from",
			to: "to",
			allWorkspaces: "all workspaces",
			workspaceLabel: "Workspace",
			agentLabel: "Agent",
			allAgents: "all agents",
			sourceLabel: "Source",
			allSources: "all sources",
			modelLabel: "Model",
			allModels: "all models",
			providerLabel: "Provider",
			allProviders: "all providers",
			onlyErrors: "only errors",
			clear: "clear filters",
			kpiCalls: "calls",
			kpiTokens: "tokens in and out",
			kpiCacheRate: "input from cache",
			kpiTurns: "{n} turn | {n} turns",
			kindLabel: "call type",
			allKinds: "every type",
			kind: {
				chat: "conversation",
				embedding: "embedding",
				image: "image",
				speech: "speech",
				transcription: "transcription",
			},
			kpiCost: "cost",
			kpiCredits: "credits charged",
			kpiAnswers: "{n} answer charged | {n} answers charged",
			kpiLatency: "average latency",
			kpiErrors: "errors",
			creditsNote:
				"Credits count the whole window of each workspace: the agent, model and error cuts do not reach the ledger.",
			chartTitle: "cost by day",
			chartBar: "{day} · {money} · {n} turns",
			byWorkspace: "by workspace",
			byAgent: "by agent",
			byModel: "by model",
			byTool: "by tool — where the time went",
			byToolHint: "calls, time and errors; a tool call has no price to print",
			credits: "credits",
			latency: "latency",
			agent: "agent",
			provider: "provider",
			tool: "tool",
			calls: "calls",
			time: "time",
			cacheShort: "cache",
			cacheRead: "read",
			cacheWrite: "write",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "new",
			reasoning: "reasoning",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Charged by cost: {cost} × {rate} = {credits} cr",
			chargeByTier: "Fixed {tier} price: {credits} cr",
			chargeTools: "+ {n} cr in tools",
			chargeNone: "Nothing charged",
			areaAttendant: "attendant",
			areaBen: "Ben",
			loopNote:
				"{calls} calls in this turn: each one re-sends the whole conversation, which is how the API works. {pct}% of that came from cache.",
			groupedIn: "Days cut in {zone}.",
		},
		/**
		 * O histórico chamada-a-chamada — a emenda de 14/08/2026: "tem que mostrar
		 * de todos com os filtros". O conteúdo viaja; as frases em volta são daqui.
		 */
		history: {
			title: "History",
			lead: "Call by call, across every workspace — the panel's filters cut this list too.",
			searchLabel: "Search the history",
			searchPlaceholder: "search text, agent or workspace",
			inFilter: "{n} call in this cut | {n} calls in this cut",
			failed: "failed",
			noModel: "no model",
			empty: "No call in the period with these filters.",
			previous: "previous",
			next: "next",
			pageOf: "page {page} of {total}",
			asked: "what was asked",
			answered: "what was answered",
			tools: "tools",
			noTools: "no tool was called",
			toolIn: "input",
			toolOut: "output",
			error: "error",
			opening: "opening the call…",
			openFailed: "Could not open this call.",
			ratingNote: "note",
			ratedDown: "marked wrong",
			ratedUp: "marked right",
			calls: "calls to the model",
			noCalls: "No call recorded for this turn — it ran before the per-call log existed.",
			callFallback: "link {n}",
			systemPrompt: "system prompt",
			units: "units",
			conversation: "Conversation",
			objective: "objective",
			missionPreamble: "the mission's fixed instructions",
			noReply: "No text reply: the work went through {n} tools.",
			noReplyShort: "no text reply · {n} tools",
			interrupted: "interrupted",
			seeTools: "See the tools",
			previousTurn: "Previous turn",
			nextTurn: "Next turn",
			close: "Close",
			source: {
				panel: "panel",
				channel: "channel",
				mission: "mission",
				automation: "automation",
			},
		},
		errors: {
			title: "Errors",
			scopeOpen: "open",
			scopeAll: "everything",
			lead: "One line per distinct failure, with how many times it happened. Durable — a restart does not clear it.",
			emptyOpen: "Nothing open. Which is what one hopes for from an error screen.",
			emptyAll: "No error recorded.",
			markFailed: "Could not mark that error.",
			reopen: "reopen",
			resolve: "resolve",
			last: "last {ago}",
			first: "first {ago}",
			cameBack: "came back after being resolved",
			hideStack: "hide the stack",
			showStack: "show the stack",
			now: "now",
			minutesAgo: "{n} min ago",
			hoursAgo: "{n} h ago",
			daysAgo: "{n} d ago",
		},
		tenants: {
			title: "Workspaces",
			searchPlaceholder: "search by name, slug or e-mail",
			searchLabel: "Search workspace",
			filterAll: "all",
			filterActive: "active",
			filterSuspended: "suspended",
			filterArchived: "archived",
			total: "{n} in total",
			workspace: "workspace",
			country: "country",
			people: "people",
			agents: "agents",
			turns30d: "turns 30d",
			cost30d: "cost 30d",
			credits: "credits",
			creditsBlocked: "blocked",
			sites: "Pages",
			plan: "plan",
			channels: "channels",
			messages30d: "msgs 30d",
			kindAll: "all types",
			kinds: {
				trial: "trial",
				courtesy: "courtesy",
				paid: "paying",
				free: "free",
				ended: "ended",
				none: "no plan",
			},
			trialUntil: "trial until {date}",
			peek: "Open as the owner in a new tab",
			peekAs: "Open as {name} in a new tab",
			peekFailed: "Could not open the account.",
			sortBy: "Sort by {column}",
			detail: {
				loading: "Loading…",
				channels: "Channels",
				noChannels: "No channel.",
				connectedAt: "connected {when}",
				people: "People",
				structure: "Structure",
				teams: "Teams",
				week: "Last 7 days",
				openConversations: "open conversations",
				agentTurns: "agent turns",
				automationRuns: "automation runs",
				errors: "errors",
				lastMessage: "last message",
				openFull: "Open full record",
				builtKinds: {
					entity: "Records",
					agent: "Agents",
					pipeline: "Funnels",
					automation: "Automations",
					artifact: "Artifacts",
				},
			},
			sitesEmpty: "No page created.",
			siteDraft: "draft",
			siteDefault: "main",
			siteNoAddress: "no address yet",
			activity: "activity",
			state: "state",
			never: "never",
			now: "now",
			reactivate: "reactivate",
			suspend: "suspend",
			archive: "archive",
			actionReactivate: "Reactivate",
			actionSuspend: "Suspend",
			actionArchive: "Archive",
			purge: "delete forever",
			actionPurge: "Delete forever",
			consequenceArchive: "Nobody comes in any more and the workspace leaves the active list.",
			consequenceSuspend: "Nobody comes in and nothing leaves it while it is suspended.",
			consequencePurge:
				"Deletes this workspace and everything in it — people, conversations, contacts, files, history. There is no way back.",
			typeToConfirm: "Type {slug} to confirm.",
			typeLabel: "Type {slug} to confirm",
			empty: "No workspace matches this filter.",
			actionFailed: "Could not change that workspace's state.",
			purgeFailed: "Could not delete that workspace. It stays archived — nothing was touched.",
			bulkSelected: "{n} selected",
			bulkPurge: "delete all selected",
			bulkConsequence:
				"This archives and deletes FOR GOOD every workspace listed — rows, files and tables. There is no way back.",
			bulkType: "Type {word} to confirm.",
			bulkWord: "DELETE",
			bulkFailed: "Could not delete: {slugs}. The others were deleted.",
			bulkDone: "All selected workspaces were deleted.",
		},
	},
	"pt-BR": {
		credits: {
			title: "Créditos na mão",
			balance: "plano {plan} · bônus {bonus}",
			blocked: "IA bloqueada neste workspace",
			amount: "Créditos",
			amountHint: "Negativo retira.",
			pool: "Bolso",
			poolBonus: "Bônus (sobrevive ao ciclo)",
			poolPlan: "Plano (zera no fechamento)",
			reason: "Por quê — pelo menos dez caracteres",
			apply: "Aplicar",
			applied: "{n} créditos aplicados. O saldo agora é {total}.",
			reasonShort:
				"O motivo precisa de pelo menos dez caracteres — é ele que explica esta linha daqui a meses.",
			blockTitle: "Parada manual",
			blockLead:
				"Sobrevive a toda recarga: encher os créditos não a desfaz. Só o que está aqui a levanta.",
			blockReason: "Por que bloquear (ou soltar) — pelo menos dez caracteres",
			block: "Bloquear a IA",
			unblock: "Levantar o bloqueio",
			blockedSince: "Bloqueado: {reason}",
			historyTitle: "O que já se mexeu aqui",
			historyEmpty: "Ninguém mexeu neste saldo à mão ainda.",
			historyNone: "sem motivo registrado",
			kindAdjustment: "ajuste",
			kindBlock: "bloqueio",
			kindUnblock: "desbloqueio",
		},
		audit: {
			title: "Auditoria",
			lead: "Toda ação de plataforma, gravada em tabela — sobrevive a um reinício.",
			empty: "Nada foi feito nesta instalação ainda.",
			someone: "alguém",
			aWorkspace: "um workspace",
			statusChange: "{who} mudou {what} de {from} para {to}",
			grant: "{who} abriu uma sessão de suporte em {what} por {minutes} min",
			revoke: "{who} revogou a sessão de suporte de {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Presença",
			readyOf: "{ready} de {total} aptos",
			ready: "apto",
			openConversations: "{n} em aberto",
		},
		notice: {
			title: "Avisos",
			new: "novo aviso",
			message: "Mensagem",
			target: "alvo",
			allWorkspaces: "todos os workspaces",
			duration: "por",
			tone: "cor",
			for1h: "1 hora",
			for1d: "1 dia",
			for1w: "1 semana",
			for30d: "30 dias",
			publish: "publicar",
			link: "Link (opcional)",
			open: "abrir",
			empty: "Nenhum aviso ativo.",
			createFailed: "Não deu para publicar o aviso.",
			revokeFailed: "Não deu para revogar o aviso.",
			bannerUntil: "até {time}",
		},
		support: {
			bannerUntil: "O suporte da plataforma pode ver este workspace até {time}.",
			bannerReason: "Motivo: “{reason}”",
			bannerGrantedBy: " — concedido por {name}",
			openTitle: "Sessões de suporte abertas",
			endsIn: "“{reason}” — {who}, termina em {remaining}",
			expiring: "expirando",
			revoke: "Revogar",
			revokeFailed: "Não deu para revogar essa sessão.",
			copyLink: "copiar link de acesso",
			linkCopied: "Link copiado — quem o possuir entra como admin até o prazo.",
			grantFailed: "Não deu para abrir a sessão de suporte.",
			activeUntil: "Sessão de suporte aberta até {until} — “{reason}”.",
			bandNote: "Este workspace está vendo uma faixa sobre isso.",
			reason: "Motivo — o workspace vai ler esta frase",
			reasonPlaceholder: "por que alguém precisa olhar este workspace",
			forHowLong: "Por",
			open: "Abrir sessão",
		},
		usage: {
			tabs: {
				label: "Vistas do consumo",
				history: "Histórico",
				panel: "Painel",
				workspaces: "Por workspace",
			},
			title: "Uso",
			period: "Período",
			days: "{n} dias",
			inPeriod: "{money} no período",
			workspace: "workspace",
			day: "dia",
			model: "modelo",
			turns: "turnos",
			errors: "erros",
			tokens: "tokens",
			cost: "custo",
			models: "modelos",
			empty:
				"Nenhum turno no período. Se isso parece errado, é uma pergunta para o log — a leitura roda com o contexto de cada workspace, nunca por fora dele.",
			from: "de",
			to: "até",
			allWorkspaces: "todos os workspaces",
			workspaceLabel: "Workspace",
			agentLabel: "Agente",
			allAgents: "todos os agentes",
			sourceLabel: "Origem",
			allSources: "todas as origens",
			modelLabel: "Modelo",
			allModels: "todos os modelos",
			providerLabel: "Provedor",
			allProviders: "todos os provedores",
			onlyErrors: "só erros",
			clear: "limpar filtros",
			kpiCalls: "chamadas",
			kpiTokens: "tokens entrada e saída",
			kpiCacheRate: "entrada do cache",
			kpiTurns: "{n} turno | {n} turnos",
			kindLabel: "tipo de chamada",
			allKinds: "todos os tipos",
			kind: {
				chat: "conversa",
				embedding: "embedding",
				image: "imagem",
				speech: "voz",
				transcription: "transcrição",
			},
			kpiCost: "custo",
			kpiCredits: "créditos cobrados",
			kpiAnswers: "{n} resposta cobrada | {n} respostas cobradas",
			kpiLatency: "latência média",
			kpiErrors: "erros",
			creditsNote:
				"Os créditos contam a janela inteira de cada workspace: os recortes de agente, modelo e erro não alcançam o razão.",
			chartTitle: "custo por dia",
			chartBar: "{day} · {money} · {n} turnos",
			byWorkspace: "por workspace",
			byAgent: "por agente",
			byModel: "por modelo",
			byTool: "por ferramenta — onde foi o tempo",
			byToolHint: "chamadas, tempo e erros; chamada de ferramenta não tem preço para imprimir",
			credits: "créditos",
			latency: "latência",
			agent: "agente",
			provider: "provedor",
			tool: "ferramenta",
			calls: "chamadas",
			time: "tempo",
			cacheShort: "cache",
			cacheRead: "leitura",
			cacheWrite: "escrita",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "nova",
			reasoning: "raciocínio",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Cobrado pelo custo: {cost} × {rate} = {credits} cr",
			chargeByTier: "Preço fixo do nível {tier}: {credits} cr",
			chargeTools: "+ {n} cr em ferramentas",
			chargeNone: "Nada cobrado",
			areaAttendant: "atendente",
			areaBen: "Ben",
			loopNote:
				"{calls} chamadas neste turno: cada uma reenvia a conversa inteira, que é como a API funciona. {pct}% disso veio do cache.",
			groupedIn: "Dias cortados em {zone}.",
		},
		history: {
			title: "Histórico",
			lead: "Chamada a chamada, de todos os workspaces — os filtros do painel cortam esta lista também.",
			searchLabel: "Buscar no histórico",
			searchPlaceholder: "buscar por texto, agente ou workspace",
			inFilter: "{n} chamada neste recorte | {n} chamadas neste recorte",
			failed: "falhou",
			noModel: "sem modelo",
			empty: "Nenhuma chamada no período com esses filtros.",
			previous: "anterior",
			next: "próxima",
			pageOf: "página {page} de {total}",
			asked: "o que foi perguntado",
			answered: "o que foi respondido",
			tools: "ferramentas",
			noTools: "nenhuma ferramenta foi chamada",
			toolIn: "entrada",
			toolOut: "saída",
			error: "erro",
			opening: "abrindo a chamada…",
			openFailed: "Não deu para abrir essa chamada.",
			ratingNote: "nota",
			ratedDown: "reprovados",
			ratedUp: "aprovados",
			calls: "chamadas ao modelo",
			noCalls:
				"Nenhuma chamada registrada neste turno — ele correu antes de o registro por chamada existir.",
			callFallback: "elo {n}",
			systemPrompt: "prompt de sistema",
			units: "unidades",
			conversation: "Conversa",
			objective: "objetivo",
			missionPreamble: "instruções fixas da missão",
			noReply: "Sem texto de resposta: o trabalho foi feito em {n} ferramentas.",
			noReplyShort: "sem texto de resposta · {n} ferramentas",
			interrupted: "interrompido",
			seeTools: "Ver as ferramentas",
			previousTurn: "Turno anterior",
			nextTurn: "Próximo turno",
			close: "Fechar",
			source: {
				panel: "painel",
				channel: "canal",
				mission: "missão",
				automation: "automação",
			},
		},
		errors: {
			title: "Erros",
			scopeOpen: "em aberto",
			scopeAll: "tudo",
			lead: "Uma linha por falha distinta, com o número de vezes que aconteceu. Durável — um reinício não apaga.",
			emptyOpen: "Nada em aberto. É o que se espera de uma tela de erros.",
			emptyAll: "Nenhum erro registrado.",
			markFailed: "Não deu para marcar esse erro.",
			reopen: "reabrir",
			resolve: "resolver",
			last: "última {ago}",
			first: "primeira {ago}",
			cameBack: "voltou depois de resolvido",
			hideStack: "esconder pilha",
			showStack: "ver pilha",
			now: "agora",
			minutesAgo: "há {n} min",
			hoursAgo: "há {n} h",
			daysAgo: "há {n} d",
		},
		tenants: {
			title: "Workspaces",
			searchPlaceholder: "buscar por nome, slug ou e-mail",
			searchLabel: "Buscar workspace",
			filterAll: "todos",
			filterActive: "ativos",
			filterSuspended: "suspensos",
			filterArchived: "arquivados",
			total: "{n} no total",
			workspace: "workspace",
			country: "país",
			people: "pessoas",
			agents: "agentes",
			turns30d: "turnos 30d",
			cost30d: "custo 30d",
			credits: "créditos",
			creditsBlocked: "bloqueado",
			sites: "Páginas",
			plan: "plano",
			channels: "canais",
			messages30d: "msgs 30d",
			kindAll: "todos os tipos",
			kinds: {
				trial: "teste",
				courtesy: "cortesia",
				paid: "pagantes",
				free: "grátis",
				ended: "vencidas",
				none: "sem plano",
			},
			trialUntil: "teste até {date}",
			peek: "Abrir como o dono numa aba nova",
			peekAs: "Abrir como {name} numa aba nova",
			peekFailed: "Não deu para abrir a conta.",
			sortBy: "Ordenar por {column}",
			detail: {
				loading: "Carregando…",
				channels: "Canais",
				noChannels: "Nenhum canal.",
				connectedAt: "conectado {when}",
				people: "Pessoas",
				structure: "Estrutura",
				teams: "Times",
				week: "Últimos 7 dias",
				openConversations: "conversas abertas",
				agentTurns: "turnos do agente",
				automationRuns: "automações rodadas",
				errors: "erros",
				lastMessage: "última mensagem",
				openFull: "Abrir ficha completa",
				builtKinds: {
					entity: "Cadastros",
					agent: "Agentes",
					pipeline: "Funis",
					automation: "Automações",
					artifact: "Artefatos",
				},
			},
			sitesEmpty: "Nenhuma página criada.",
			siteDraft: "rascunho",
			siteDefault: "principal",
			siteNoAddress: "sem endereço ainda",
			activity: "atividade",
			state: "estado",
			never: "nunca",
			now: "agora",
			reactivate: "reativar",
			suspend: "suspender",
			archive: "arquivar",
			actionReactivate: "Reativar",
			actionSuspend: "Suspender",
			actionArchive: "Arquivar",
			purge: "excluir de vez",
			actionPurge: "Excluir de vez",
			consequenceArchive: "Ninguém mais entra e o workspace sai da lista de ativos.",
			consequenceSuspend: "Ninguém entra e nada sai dele enquanto estiver suspenso.",
			consequencePurge:
				"Apaga este workspace e tudo o que há nele — pessoas, conversas, contatos, arquivos, histórico. Não tem volta.",
			typeToConfirm: "Digite {slug} para confirmar.",
			typeLabel: "Digite {slug} para confirmar",
			empty: "Nenhum workspace com esse filtro.",
			actionFailed: "Não deu para mudar o estado desse workspace.",
			purgeFailed: "Não deu para excluir esse workspace. Ele segue arquivado — nada foi tocado.",
			bulkSelected: "{n} selecionado | {n} selecionados",
			bulkPurge: "excluir todos os selecionados",
			bulkConsequence:
				"Isto arquiva e apaga DE VEZ os workspaces listados — dados, arquivos e tabelas. Sem volta.",
			bulkType: "Digite {word} para confirmar.",
			bulkWord: "EXCLUIR",
			bulkFailed: "Não deu para excluir: {slugs}. Os demais foram excluídos.",
			bulkDone: "Todos os workspaces selecionados foram excluídos.",
		},
	},
	el: {
		credits: {
			title: "Πιστώσεις, στο χέρι",
			balance: "πλάνο {plan} · δώρο {bonus}",
			blocked: "Το AI είναι μπλοκαρισμένο σε αυτόν τον χώρο εργασίας",
			amount: "Πιστώσεις",
			amountHint: "Αρνητικό αφαιρεί.",
			pool: "Τσέπη",
			poolBonus: "Δώρο (επιβιώνει του κύκλου)",
			poolPlan: "Πλάνο (μηδενίζει στο κλείσιμο)",
			reason: "Γιατί — τουλάχιστον δέκα χαρακτήρες",
			apply: "Εφαρμογή",
			applied: "Εφαρμόστηκαν {n} πιστώσεις. Το υπόλοιπο είναι τώρα {total}.",
			reasonShort:
				"Ο λόγος χρειάζεται τουλάχιστον δέκα χαρακτήρες — αυτός εξηγεί αυτή τη γραμμή σε μερικούς μήνες.",
			blockTitle: "Χειροκίνητη διακοπή",
			blockLead:
				"Επιβιώνει κάθε ανανέωσης: η προσθήκη πιστώσεων δεν την αναιρεί. Μόνο ό,τι είναι εδώ την αίρει.",
			blockReason: "Γιατί να μπλοκαριστεί (ή να αρθεί) — τουλάχιστον δέκα χαρακτήρες",
			block: "Μπλοκάρισμα του AI",
			unblock: "Άρση του μπλοκαρίσματος",
			blockedSince: "Μπλοκαρισμένο: {reason}",
			historyTitle: "Τι έχει μετακινηθεί εδώ",
			historyEmpty: "Κανείς δεν έχει πειράξει αυτό το υπόλοιπο στο χέρι ακόμη.",
			historyNone: "χωρίς καταγεγραμμένο λόγο",
			kindAdjustment: "προσαρμογή",
			kindBlock: "μπλοκάρισμα",
			kindUnblock: "ξεμπλοκάρισμα",
		},
		audit: {
			title: "Έλεγχος",
			lead: "Κάθε ενέργεια της πλατφόρμας, αποθηκευμένη σε πίνακα — επιβιώνει μιας επανεκκίνησης.",
			empty: "Δεν έχει γίνει τίποτα σε αυτή την εγκατάσταση ακόμη.",
			someone: "κάποιος",
			aWorkspace: "ένας χώρος εργασίας",
			statusChange: "{who} μετακίνησε το {what} από {from} σε {to}",
			grant: "{who} άνοιξε συνεδρία υποστήριξης στο {what} για {minutes} λ.",
			revoke: "{who} ανακάλεσε τη συνεδρία υποστήριξης στο {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Παρουσία",
			readyOf: "{ready} από {total} έτοιμοι",
			ready: "έτοιμος",
			openConversations: "{n} ανοιχτές",
		},
		notice: {
			title: "Ανακοινώσεις",
			new: "νέα ανακοίνωση",
			message: "Μήνυμα",
			target: "στόχος",
			allWorkspaces: "όλα τα εργαστήρια",
			duration: "για",
			tone: "χρώμα",
			for1h: "1 ώρα",
			for1d: "1 ημέρα",
			for1w: "1 εβδομάδα",
			for30d: "30 ημέρες",
			publish: "δημοσίευση",
			link: "Σύνδεσμος (προαιρετικό)",
			open: "άνοιγμα",
			empty: "Καμία ενεργή ανακοίνωση.",
			createFailed: "Δεν ήταν δυνατή η δημοσίευση.",
			revokeFailed: "Δεν ήταν δυνατή η ανάκληση.",
			bannerUntil: "έως {time}",
		},
		support: {
			bannerUntil:
				"Η υποστήριξη της πλατφόρμας μπορεί να βλέπει αυτόν τον χώρο εργασίας μέχρι τις {time}.",
			bannerReason: "Λόγος: «{reason}»",
			bannerGrantedBy: " — παραχωρήθηκε από {name}",
			openTitle: "Ανοιχτές συνεδρίες υποστήριξης",
			endsIn: "«{reason}» — {who}, τελειώνει σε {remaining}",
			expiring: "λήγει",
			revoke: "Ανάκληση",
			revokeFailed: "Δεν ήταν δυνατή η ανάκληση αυτής της συνεδρίας.",
			copyLink: "αντιγραφή συνδέσμου πρόσβασης",
			linkCopied: "Ο σύνδεσμος αντιγράφηκε — όποιος τον έχει μπαίνει ως admin μέχρι την προθεσμία.",
			grantFailed: "Δεν ήταν δυνατό το άνοιγμα της συνεδρίας υποστήριξης.",
			activeUntil: "Συνεδρία υποστήριξης ανοιχτή μέχρι τις {until} — «{reason}».",
			bandNote: "Αυτός ο χώρος εργασίας βλέπει μια λωρίδα γι' αυτό.",
			reason: "Λόγος — ο χώρος εργασίας θα διαβάσει αυτή τη φράση",
			reasonPlaceholder: "γιατί χρειάζεται κάποιος να δει αυτόν τον χώρο εργασίας",
			forHowLong: "Για",
			open: "Άνοιγμα της συνεδρίας",
		},
		usage: {
			tabs: {
				label: "Όψεις κατανάλωσης",
				history: "Ιστορικό",
				panel: "Πίνακας",
				workspaces: "Ανά χώρο εργασίας",
			},
			title: "Κατανάλωση",
			period: "Περίοδος",
			days: "{n} ημέρες",
			inPeriod: "{money} στην περίοδο",
			workspace: "χώρος εργασίας",
			day: "ημέρα",
			model: "μοντέλο",
			turns: "γύροι",
			errors: "σφάλματα",
			tokens: "tokens",
			cost: "κόστος",
			models: "μοντέλα",
			empty:
				"Κανένας γύρος στην περίοδο. Αν αυτό φαίνεται λάθος, είναι ερώτηση για το αρχείο καταγραφής — η ανάγνωση τρέχει μέσα στο δικό του πλαίσιο κάθε χώρου εργασίας, ποτέ έξω από αυτό.",
			from: "από",
			to: "έως",
			allWorkspaces: "όλοι οι χώροι εργασίας",
			workspaceLabel: "Χώρος εργασίας",
			agentLabel: "Πράκτορας",
			allAgents: "όλοι οι πράκτορες",
			sourceLabel: "Προέλευση",
			allSources: "όλες οι προελεύσεις",
			modelLabel: "Μοντέλο",
			allModels: "όλα τα μοντέλα",
			providerLabel: "Πάροχος",
			allProviders: "όλοι οι πάροχοι",
			onlyErrors: "μόνο σφάλματα",
			clear: "καθαρισμός φίλτρων",
			kpiCalls: "κλήσεις",
			kpiTokens: "tokens εισόδου και εξόδου",
			kpiCacheRate: "είσοδος από cache",
			kpiTurns: "{n} γύρος | {n} γύροι",
			kindLabel: "τύπος κλήσης",
			allKinds: "όλοι οι τύποι",
			kind: {
				chat: "συνομιλία",
				embedding: "ενσωμάτωση",
				image: "εικόνα",
				speech: "ομιλία",
				transcription: "μεταγραφή",
			},
			kpiCost: "κόστος",
			kpiCredits: "πιστώσεις που χρεώθηκαν",
			kpiAnswers: "{n} απάντηση χρεώθηκε | {n} απαντήσεις χρεώθηκαν",
			kpiLatency: "μέση καθυστέρηση",
			kpiErrors: "σφάλματα",
			creditsNote:
				"Οι πιστώσεις μετρούν όλο το παράθυρο κάθε χώρου εργασίας: οι τομές πράκτορα, μοντέλου και σφάλματος δεν φτάνουν στο καθολικό.",
			chartTitle: "κόστος ανά ημέρα",
			chartBar: "{day} · {money} · {n} γύροι",
			byWorkspace: "ανά χώρο εργασίας",
			byAgent: "ανά πράκτορα",
			byModel: "ανά μοντέλο",
			byTool: "ανά εργαλείο — πού πήγε ο χρόνος",
			byToolHint: "κλήσεις, χρόνος και σφάλματα· η κλήση εργαλείου δεν έχει τιμή να τυπωθεί",
			credits: "πιστώσεις",
			latency: "καθυστέρηση",
			agent: "πράκτορας",
			provider: "πάροχος",
			tool: "εργαλείο",
			calls: "κλήσεις",
			time: "χρόνος",
			cacheShort: "cache",
			cacheRead: "ανάγνωση",
			cacheWrite: "εγγραφή",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "νέα",
			reasoning: "συλλογισμός",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Χρέωση με βάση το κόστος: {cost} × {rate} = {credits} cr",
			chargeByTier: "Σταθερή τιμή επιπέδου {tier}: {credits} cr",
			chargeTools: "+ {n} cr σε εργαλεία",
			chargeNone: "Καμία χρέωση",
			areaAttendant: "βοηθός",
			areaBen: "Ben",
			loopNote:
				"{calls} κλήσεις σε αυτόν τον γύρο: καθεμία ξαναστέλνει όλη τη συνομιλία, έτσι λειτουργεί το API. Το {pct}% ήρθε από την κρυφή μνήμη.",
			groupedIn: "Οι ημέρες κόβονται σε {zone}.",
		},
		history: {
			title: "Ιστορικό",
			lead: "Κλήση προς κλήση, σε όλους τους χώρους εργασίας — τα φίλτρα του πάνελ κόβουν και αυτή τη λίστα.",
			searchLabel: "Αναζήτηση στο ιστορικό",
			searchPlaceholder: "αναζήτηση σε κείμενο, πράκτορα ή χώρο εργασίας",
			inFilter: "{n} κλήση σε αυτή την τομή | {n} κλήσεις σε αυτή την τομή",
			failed: "απέτυχε",
			noModel: "χωρίς μοντέλο",
			empty: "Καμία κλήση στην περίοδο με αυτά τα φίλτρα.",
			previous: "προηγούμενη",
			next: "επόμενη",
			pageOf: "σελίδα {page} από {total}",
			asked: "τι ρωτήθηκε",
			answered: "τι απαντήθηκε",
			tools: "εργαλεία",
			noTools: "δεν κλήθηκε κανένα εργαλείο",
			toolIn: "είσοδος",
			toolOut: "έξοδος",
			error: "σφάλμα",
			opening: "άνοιγμα της κλήσης…",
			openFailed: "Δεν ήταν δυνατό το άνοιγμα αυτής της κλήσης.",
			ratingNote: "σημείωση",
			ratedDown: "σημειωμένα ως λάθος",
			ratedUp: "σημειωμένα ως σωστά",
			calls: "κλήσεις στο μοντέλο",
			noCalls:
				"Καμία καταγεγραμμένη κλήση σε αυτόν τον γύρο — έτρεξε πριν υπάρξει το ημερολόγιο ανά κλήση.",
			callFallback: "κρίκος {n}",
			systemPrompt: "προτροπή συστήματος",
			units: "μονάδες",
			conversation: "Συνομιλία",
			objective: "στόχος",
			missionPreamble: "οι σταθερές οδηγίες της αποστολής",
			noReply: "Χωρίς απάντηση κειμένου: η δουλειά έγινε με {n} εργαλεία.",
			noReplyShort: "χωρίς απάντηση κειμένου · {n} εργαλεία",
			interrupted: "διακόπηκε",
			seeTools: "Δείτε τα εργαλεία",
			previousTurn: "Προηγούμενος γύρος",
			nextTurn: "Επόμενος γύρος",
			close: "Κλείσιμο",
			source: {
				panel: "πάνελ",
				channel: "κανάλι",
				mission: "αποστολή",
				automation: "αυτοματισμός",
			},
		},
		errors: {
			title: "Σφάλματα",
			scopeOpen: "ανοιχτά",
			scopeAll: "όλα",
			lead: "Μία γραμμή ανά διακριτή αστοχία, με το πόσες φορές συνέβη. Διαρκές — μια επανεκκίνηση δεν το σβήνει.",
			emptyOpen: "Τίποτα ανοιχτό. Που είναι ό,τι ελπίζει κανείς από μια οθόνη σφαλμάτων.",
			emptyAll: "Κανένα καταγεγραμμένο σφάλμα.",
			markFailed: "Δεν ήταν δυνατή η σήμανση αυτού του σφάλματος.",
			reopen: "επανάνοιγμα",
			resolve: "επίλυση",
			last: "τελευταία {ago}",
			first: "πρώτη {ago}",
			cameBack: "επέστρεψε αφού είχε επιλυθεί",
			hideStack: "απόκρυψη της στοίβας",
			showStack: "εμφάνιση της στοίβας",
			now: "τώρα",
			minutesAgo: "πριν {n} λ.",
			hoursAgo: "πριν {n} ώ.",
			daysAgo: "πριν {n} ημ.",
		},
		tenants: {
			title: "Χώροι εργασίας",
			searchPlaceholder: "αναζήτηση με όνομα ή slug",
			searchLabel: "Αναζήτηση χώρου εργασίας",
			filterAll: "όλοι",
			filterActive: "ενεργοί",
			filterSuspended: "σε αναστολή",
			filterArchived: "αρχειοθετημένοι",
			total: "{n} συνολικά",
			workspace: "χώρος εργασίας",
			country: "χώρα",
			people: "άτομα",
			agents: "πράκτορες",
			turns30d: "γύροι 30ημ.",
			cost30d: "κόστος 30ημ.",
			credits: "πιστώσεις",
			creditsBlocked: "μπλοκαρισμένες",
			sites: "Σελίδες",
			plan: "plan",
			channels: "channels",
			messages30d: "msgs 30d",
			kindAll: "all types",
			kinds: {
				trial: "trial",
				courtesy: "courtesy",
				paid: "paying",
				free: "free",
				ended: "ended",
				none: "no plan",
			},
			trialUntil: "trial until {date}",
			peek: "Open as the owner in a new tab",
			peekAs: "Open as {name} in a new tab",
			peekFailed: "Could not open the account.",
			sortBy: "Sort by {column}",
			detail: {
				loading: "Loading…",
				channels: "Channels",
				noChannels: "No channel.",
				connectedAt: "connected {when}",
				people: "People",
				structure: "Structure",
				teams: "Teams",
				week: "Last 7 days",
				openConversations: "open conversations",
				agentTurns: "agent turns",
				automationRuns: "automation runs",
				errors: "errors",
				lastMessage: "last message",
				openFull: "Open full record",
				builtKinds: {
					entity: "Records",
					agent: "Agents",
					pipeline: "Funnels",
					automation: "Automations",
					artifact: "Artifacts",
				},
			},
			sitesEmpty: "Καμία σελίδα.",
			siteDraft: "πρόχειρο",
			siteDefault: "κύρια",
			siteNoAddress: "χωρίς διεύθυνση ακόμη",
			activity: "δραστηριότητα",
			state: "κατάσταση",
			never: "ποτέ",
			now: "τώρα",
			reactivate: "επανενεργοποίηση",
			suspend: "αναστολή",
			archive: "αρχειοθέτηση",
			actionReactivate: "Επανενεργοποίηση",
			actionSuspend: "Αναστολή",
			actionArchive: "Αρχειοθέτηση",
			purge: "οριστική διαγραφή",
			actionPurge: "Οριστική διαγραφή",
			consequenceArchive:
				"Κανείς δεν μπαίνει πια και ο χώρος εργασίας φεύγει από τη λίστα των ενεργών.",
			consequenceSuspend: "Κανείς δεν μπαίνει και τίποτα δεν βγαίνει όσο είναι σε αναστολή.",
			consequencePurge:
				"Διαγράφει αυτόν τον χώρο εργασίας και ό,τι υπάρχει μέσα του — άτομα, συνομιλίες, επαφές, αρχεία, ιστορικό. Δεν υπάρχει επιστροφή.",
			typeToConfirm: "Πληκτρολόγησε {slug} για επιβεβαίωση.",
			typeLabel: "Πληκτρολόγησε {slug} για επιβεβαίωση",
			empty: "Κανένας χώρος εργασίας δεν ταιριάζει με αυτό το φίλτρο.",
			actionFailed: "Δεν ήταν δυνατή η αλλαγή της κατάστασης αυτού του χώρου εργασίας.",
			purgeFailed:
				"Δεν ήταν δυνατή η διαγραφή αυτού του χώρου εργασίας. Παραμένει αρχειοθετημένος — τίποτα δεν πειράχτηκε.",
			bulkSelected: "{n} επιλεγμένα",
			bulkPurge: "οριστική διαγραφή των επιλεγμένων",
			bulkConsequence:
				"Αυτό αρχειοθετεί και διαγράφει ΟΡΙΣΤΙΚΑ όλα τα εργαστήρια της λίστας — δεδομένα, αρχεία και πίνακες. Χωρίς επιστροφή.",
			bulkType: "Πληκτρολόγησε {word} για επιβεβαίωση.",
			bulkWord: "ΔΙΑΓΡΑΦΗ",
			bulkFailed: "Δεν διαγράφηκαν: {slugs}. Τα υπόλοιπα διαγράφηκαν.",
			bulkDone: "Όλα τα επιλεγμένα διαγράφηκαν.",
		},
	},
	es: {
		credits: {
			title: "Créditos a mano",
			balance: "plan {plan} · bono {bonus}",
			blocked: "La IA está bloqueada en este espacio de trabajo",
			amount: "Créditos",
			amountHint: "En negativo, retira.",
			pool: "Bolsillo",
			poolBonus: "Bono (sobrevive al ciclo)",
			poolPlan: "Plan (se pone a cero al cerrar)",
			reason: "Por qué — al menos diez caracteres",
			apply: "Aplicar",
			applied: "{n} créditos aplicados. El saldo ahora es {total}.",
			reasonShort:
				"El motivo necesita al menos diez caracteres — es lo que explicará esta línea dentro de meses.",
			blockTitle: "Parada manual",
			blockLead:
				"Sobrevive a cualquier recarga: llenar de créditos no la deshace. Solo lo que hay aquí la levanta.",
			blockReason: "Por qué bloquear (o levantarlo) — al menos diez caracteres",
			block: "Bloquear la IA",
			unblock: "Levantar el bloqueo",
			blockedSince: "Bloqueado: {reason}",
			historyTitle: "Lo que se ha movido aquí",
			historyEmpty: "Nadie ha movido este saldo a mano todavía.",
			historyNone: "sin motivo registrado",
			kindAdjustment: "ajuste",
			kindBlock: "bloqueo",
			kindUnblock: "desbloqueo",
		},
		audit: {
			title: "Auditoría",
			lead: "Cada acción de la plataforma, guardada en una tabla — sobrevive a un reinicio.",
			empty: "Todavía no se ha hecho nada en esta instalación.",
			someone: "alguien",
			aWorkspace: "un espacio de trabajo",
			statusChange: "{who} movió {what} de {from} a {to}",
			grant: "{who} abrió una sesión de soporte en {what} durante {minutes} min",
			revoke: "{who} revocó la sesión de soporte en {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Presencia",
			readyOf: "{ready} de {total} listos",
			ready: "listo",
			openConversations: "{n} abiertas",
		},
		notice: {
			title: "Avisos",
			new: "nuevo aviso",
			message: "Mensaje",
			target: "destino",
			allWorkspaces: "todos los espacios",
			duration: "por",
			tone: "color",
			for1h: "1 hora",
			for1d: "1 día",
			for1w: "1 semana",
			for30d: "30 días",
			publish: "publicar",
			link: "Enlace (opcional)",
			open: "abrir",
			empty: "Ningún aviso activo.",
			createFailed: "No se pudo publicar el aviso.",
			revokeFailed: "No se pudo revocar el aviso.",
			bannerUntil: "hasta {time}",
		},
		support: {
			bannerUntil:
				"El soporte de la plataforma puede ver este espacio de trabajo hasta las {time}.",
			bannerReason: "Motivo: “{reason}”",
			bannerGrantedBy: " — concedido por {name}",
			openTitle: "Sesiones de soporte abiertas",
			endsIn: "“{reason}” — {who}, termina en {remaining}",
			expiring: "caducando",
			revoke: "Revocar",
			revokeFailed: "No se pudo revocar esa sesión.",
			copyLink: "copiar enlace de acceso",
			linkCopied: "Enlace copiado — quien lo tenga entra como admin hasta el plazo.",
			grantFailed: "No se pudo abrir la sesión de soporte.",
			activeUntil: "Sesión de soporte abierta hasta las {until} — “{reason}”.",
			bandNote: "Este espacio de trabajo está viendo una banda sobre esto.",
			reason: "Motivo — el espacio de trabajo leerá esta frase",
			reasonPlaceholder: "por qué alguien necesita mirar este espacio de trabajo",
			forHowLong: "Durante",
			open: "Abrir la sesión",
		},
		usage: {
			tabs: {
				label: "Vistas del consumo",
				history: "Historial",
				panel: "Panel",
				workspaces: "Por espacio",
			},
			title: "Consumo",
			period: "Periodo",
			days: "{n} días",
			inPeriod: "{money} en el periodo",
			workspace: "espacio de trabajo",
			day: "día",
			model: "modelo",
			turns: "turnos",
			errors: "errores",
			tokens: "tokens",
			cost: "coste",
			models: "modelos",
			empty:
				"Ningún turno en el periodo. Si eso parece un error, es una pregunta para el historial — la lectura corre dentro del contexto de cada espacio de trabajo, nunca fuera de él.",
			from: "desde",
			to: "hasta",
			allWorkspaces: "todos los espacios de trabajo",
			workspaceLabel: "Espacio de trabajo",
			agentLabel: "Agente",
			allAgents: "todos los agentes",
			sourceLabel: "Origen",
			allSources: "todos los orígenes",
			modelLabel: "Modelo",
			allModels: "todos los modelos",
			providerLabel: "Proveedor",
			allProviders: "todos los proveedores",
			onlyErrors: "solo errores",
			clear: "limpiar filtros",
			kpiCalls: "llamadas",
			kpiTokens: "tokens de entrada y salida",
			kpiCacheRate: "entrada desde caché",
			kpiTurns: "{n} turno | {n} turnos",
			kindLabel: "tipo de llamada",
			allKinds: "todos los tipos",
			kind: {
				chat: "conversación",
				embedding: "embedding",
				image: "imagen",
				speech: "voz",
				transcription: "transcripción",
			},
			kpiCost: "coste",
			kpiCredits: "créditos cobrados",
			kpiAnswers: "{n} respuesta cobrada | {n} respuestas cobradas",
			kpiLatency: "latencia media",
			kpiErrors: "errores",
			creditsNote:
				"Los créditos cuentan la ventana entera de cada espacio de trabajo: los cortes por agente, modelo y error no llegan al libro mayor.",
			chartTitle: "coste por día",
			chartBar: "{day} · {money} · {n} turnos",
			byWorkspace: "por espacio de trabajo",
			byAgent: "por agente",
			byModel: "por modelo",
			byTool: "por herramienta — adónde se fue el tiempo",
			byToolHint:
				"llamadas, tiempo y errores; una llamada de herramienta no tiene precio que imprimir",
			credits: "créditos",
			latency: "latencia",
			agent: "agente",
			provider: "proveedor",
			tool: "herramienta",
			calls: "llamadas",
			time: "tiempo",
			cacheShort: "caché",
			cacheRead: "lectura",
			cacheWrite: "escritura",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "nueva",
			reasoning: "razonamiento",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Cobrado por costo: {cost} × {rate} = {credits} cr",
			chargeByTier: "Precio fijo del nivel {tier}: {credits} cr",
			chargeTools: "+ {n} cr en herramientas",
			chargeNone: "Nada cobrado",
			areaAttendant: "asistente",
			areaBen: "Ben",
			loopNote:
				"{calls} llamadas en este turno: cada una reenvía toda la conversación, así funciona la API. El {pct}% vino de la caché.",
			groupedIn: "Días cortados en {zone}.",
		},
		history: {
			title: "Historial",
			lead: "Llamada a llamada, en todos los espacios de trabajo — los filtros del panel cortan esta lista también.",
			searchLabel: "Buscar en el historial",
			searchPlaceholder: "buscar por texto, agente o espacio de trabajo",
			inFilter: "{n} llamada en este corte | {n} llamadas en este corte",
			failed: "falló",
			noModel: "sin modelo",
			empty: "Ninguna llamada en el periodo con estos filtros.",
			previous: "anterior",
			next: "siguiente",
			pageOf: "página {page} de {total}",
			asked: "lo que se preguntó",
			answered: "lo que se respondió",
			tools: "herramientas",
			noTools: "no se llamó a ninguna herramienta",
			toolIn: "entrada",
			toolOut: "salida",
			error: "error",
			opening: "abriendo la llamada…",
			openFailed: "No se pudo abrir esta llamada.",
			ratingNote: "nota",
			ratedDown: "marcados como errados",
			ratedUp: "marcados como correctos",
			calls: "llamadas al modelo",
			noCalls:
				"Ninguna llamada registrada en este turno — corrió antes de que existiera el registro por llamada.",
			callFallback: "enlace {n}",
			systemPrompt: "prompt del sistema",
			units: "unidades",
			conversation: "Conversación",
			objective: "objetivo",
			missionPreamble: "instrucciones fijas de la misión",
			noReply: "Sin respuesta de texto: el trabajo se hizo con {n} herramientas.",
			noReplyShort: "sin respuesta de texto · {n} herramientas",
			interrupted: "interrumpido",
			seeTools: "Ver las herramientas",
			previousTurn: "Turno anterior",
			nextTurn: "Turno siguiente",
			close: "Cerrar",
			source: {
				panel: "panel",
				channel: "canal",
				mission: "misión",
				automation: "automatización",
			},
		},
		errors: {
			title: "Errores",
			scopeOpen: "abiertos",
			scopeAll: "todo",
			lead: "Una línea por fallo distinto, con cuántas veces pasó. Duradero — un reinicio no lo borra.",
			emptyOpen: "Nada abierto. Que es lo que se espera de una pantalla de errores.",
			emptyAll: "Ningún error registrado.",
			markFailed: "No se pudo marcar ese error.",
			reopen: "reabrir",
			resolve: "resolver",
			last: "última {ago}",
			first: "primera {ago}",
			cameBack: "volvió después de estar resuelto",
			hideStack: "ocultar la pila",
			showStack: "ver la pila",
			now: "ahora",
			minutesAgo: "hace {n} min",
			hoursAgo: "hace {n} h",
			daysAgo: "hace {n} d",
		},
		tenants: {
			title: "Espacios de trabajo",
			searchPlaceholder: "buscar por nombre o slug",
			searchLabel: "Buscar espacio de trabajo",
			filterAll: "todos",
			filterActive: "activos",
			filterSuspended: "suspendidos",
			filterArchived: "archivados",
			total: "{n} en total",
			workspace: "espacio de trabajo",
			country: "país",
			people: "personas",
			agents: "agentes",
			turns30d: "turnos 30d",
			cost30d: "coste 30d",
			credits: "créditos",
			creditsBlocked: "bloqueados",
			sites: "Páginas",
			plan: "plan",
			channels: "canales",
			messages30d: "msgs 30d",
			kindAll: "todos los tipos",
			kinds: {
				trial: "prueba",
				courtesy: "cortesía",
				paid: "de pago",
				free: "gratis",
				ended: "vencidas",
				none: "sin plan",
			},
			trialUntil: "prueba hasta {date}",
			peek: "Abrir como el dueño en una pestaña nueva",
			peekAs: "Abrir como {name} en una pestaña nueva",
			peekFailed: "No se pudo abrir la cuenta.",
			sortBy: "Ordenar por {column}",
			detail: {
				loading: "Cargando…",
				channels: "Canales",
				noChannels: "Ningún canal.",
				connectedAt: "conectado {when}",
				people: "Personas",
				structure: "Estructura",
				teams: "Equipos",
				week: "Últimos 7 días",
				openConversations: "conversaciones abiertas",
				agentTurns: "turnos del agente",
				automationRuns: "automatizaciones ejecutadas",
				errors: "errores",
				lastMessage: "último mensaje",
				openFull: "Abrir ficha completa",
				builtKinds: {
					entity: "Registros",
					agent: "Agentes",
					pipeline: "Embudos",
					automation: "Automatizaciones",
					artifact: "Artefactos",
				},
			},
			sitesEmpty: "Ninguna página creada.",
			siteDraft: "borrador",
			siteDefault: "principal",
			siteNoAddress: "sin dirección aún",
			activity: "actividad",
			state: "estado",
			never: "nunca",
			now: "ahora",
			reactivate: "reactivar",
			suspend: "suspender",
			archive: "archivar",
			actionReactivate: "Reactivar",
			actionSuspend: "Suspender",
			actionArchive: "Archivar",
			purge: "eliminar para siempre",
			actionPurge: "Eliminar para siempre",
			consequenceArchive: "Ya no entra nadie y el espacio de trabajo sale de la lista de activos.",
			consequenceSuspend: "Nadie entra y nada sale de él mientras esté suspendido.",
			consequencePurge:
				"Elimina este espacio de trabajo y todo lo que hay dentro — personas, conversaciones, contactos, archivos, historial. No hay vuelta atrás.",
			typeToConfirm: "Escribe {slug} para confirmar.",
			typeLabel: "Escribe {slug} para confirmar",
			empty: "Ningún espacio de trabajo con este filtro.",
			actionFailed: "No se pudo cambiar el estado de ese espacio de trabajo.",
			purgeFailed: "No se pudo eliminar ese espacio de trabajo. Sigue archivado — no se tocó nada.",
			bulkSelected: "{n} seleccionado | {n} seleccionados",
			bulkPurge: "eliminar todos los seleccionados",
			bulkConsequence:
				"Esto archiva y elimina PARA SIEMPRE los espacios listados — datos, archivos y tablas. Sin vuelta atrás.",
			bulkType: "Escribe {word} para confirmar.",
			bulkWord: "ELIMINAR",
			bulkFailed: "No se pudo eliminar: {slugs}. Los demás fueron eliminados.",
			bulkDone: "Todos los seleccionados fueron eliminados.",
		},
	},
	fr: {
		credits: {
			title: "Crédits à la main",
			balance: "forfait {plan} · bonus {bonus}",
			blocked: "L'IA est bloquée pour cet espace de travail",
			amount: "Crédits",
			amountHint: "Un montant négatif reprend.",
			pool: "Poche",
			poolBonus: "Bonus (survit au cycle)",
			poolPlan: "Forfait (remis à zéro à la clôture)",
			reason: "Pourquoi — au moins dix caractères",
			apply: "Appliquer",
			applied: "{n} crédits appliqués. Le solde est maintenant {total}.",
			reasonShort:
				"La raison a besoin d'au moins dix caractères — c'est elle qui expliquera cette ligne dans quelques mois.",
			blockTitle: "Arrêt manuel",
			blockLead:
				"Il survit à toute recharge : ajouter des crédits ne le défait pas. Seul ce qui est ici le lève.",
			blockReason: "Pourquoi bloquer (ou lever) — au moins dix caractères",
			block: "Bloquer l'IA",
			unblock: "Lever le blocage",
			blockedSince: "Bloqué : {reason}",
			historyTitle: "Ce qui a été bougé ici",
			historyEmpty: "Personne n'a encore touché ce solde à la main.",
			historyNone: "aucune raison enregistrée",
			kindAdjustment: "ajustement",
			kindBlock: "blocage",
			kindUnblock: "déblocage",
		},
		audit: {
			title: "Audit",
			lead: "Chaque action de plateforme, enregistrée dans une table — elle survit à un redémarrage.",
			empty: "Rien n'a encore été fait sur cette installation.",
			someone: "quelqu'un",
			aWorkspace: "un espace de travail",
			statusChange: "{who} a fait passer {what} de {from} à {to}",
			grant: "{who} a ouvert une session de support sur {what} pour {minutes} min",
			revoke: "{who} a révoqué la session de support sur {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Présence",
			readyOf: "{ready} sur {total} prêts",
			ready: "prêt",
			openConversations: "{n} en cours",
		},
		notice: {
			title: "Annonces",
			new: "nouvelle annonce",
			message: "Message",
			target: "cible",
			allWorkspaces: "tous les espaces",
			duration: "pendant",
			tone: "couleur",
			for1h: "1 heure",
			for1d: "1 jour",
			for1w: "1 semaine",
			for30d: "30 jours",
			publish: "publier",
			link: "Lien (facultatif)",
			open: "ouvrir",
			empty: "Aucune annonce active.",
			createFailed: "Impossible de publier l’annonce.",
			revokeFailed: "Impossible de révoquer l’annonce.",
			bannerUntil: "jusqu’à {time}",
		},
		support: {
			bannerUntil: "Le support de la plateforme peut voir cet espace de travail jusqu'à {time}.",
			bannerReason: "Raison : “{reason}”",
			bannerGrantedBy: " — accordé par {name}",
			openTitle: "Sessions de support ouvertes",
			endsIn: "“{reason}” — {who}, se termine dans {remaining}",
			expiring: "bientôt finie",
			revoke: "Révoquer",
			revokeFailed: "Impossible de révoquer cette session.",
			copyLink: "copier le lien d’accès",
			linkCopied: "Lien copié — quiconque le détient entre comme admin jusqu’à l’échéance.",
			grantFailed: "Impossible d'ouvrir la session de support.",
			activeUntil: "Session de support ouverte jusqu'à {until} — “{reason}”.",
			bandNote: "Cet espace de travail voit un bandeau à ce sujet.",
			reason: "Raison — l'espace de travail lira cette phrase",
			reasonPlaceholder: "pourquoi quelqu'un a besoin de regarder cet espace de travail",
			forHowLong: "Pour",
			open: "Ouvrir la session",
		},
		usage: {
			tabs: {
				label: "Vues de la consommation",
				history: "Historique",
				panel: "Tableau",
				workspaces: "Par espace",
			},
			title: "Consommation",
			period: "Période",
			days: "{n} jours",
			inPeriod: "{money} sur la période",
			workspace: "espace de travail",
			day: "jour",
			model: "modèle",
			turns: "échanges",
			errors: "erreurs",
			tokens: "tokens",
			cost: "coût",
			models: "modèles",
			empty:
				"Aucun échange sur la période. Si cela paraît faux, c'est une question pour le journal — la lecture tourne dans le contexte propre à chaque espace de travail, jamais en dehors.",
			from: "de",
			to: "à",
			allWorkspaces: "tous les espaces de travail",
			workspaceLabel: "Espace de travail",
			agentLabel: "Agent",
			allAgents: "tous les agents",
			sourceLabel: "Origine",
			allSources: "toutes les origines",
			modelLabel: "Modèle",
			allModels: "tous les modèles",
			providerLabel: "Fournisseur",
			allProviders: "tous les fournisseurs",
			onlyErrors: "erreurs seulement",
			clear: "effacer les filtres",
			kpiCalls: "appels",
			kpiTokens: "tokens en entrée et en sortie",
			kpiCacheRate: "entrée depuis le cache",
			kpiTurns: "{n} tour | {n} tours",
			kindLabel: "type d'appel",
			allKinds: "tous les types",
			kind: {
				chat: "conversation",
				embedding: "embedding",
				image: "image",
				speech: "voix",
				transcription: "transcription",
			},
			kpiCost: "coût",
			kpiCredits: "crédits facturés",
			kpiAnswers: "{n} réponse facturée | {n} réponses facturées",
			kpiLatency: "latence moyenne",
			kpiErrors: "erreurs",
			creditsNote:
				"Les crédits comptent toute la fenêtre de chaque espace de travail : les coupes par agent, modèle et erreur n'atteignent pas le grand livre.",
			chartTitle: "coût par jour",
			chartBar: "{day} · {money} · {n} échanges",
			byWorkspace: "par espace de travail",
			byAgent: "par agent",
			byModel: "par modèle",
			byTool: "par outil — où est passé le temps",
			byToolHint: "appels, temps et erreurs ; un appel d'outil n'a pas de prix à afficher",
			credits: "crédits",
			latency: "latence",
			agent: "agent",
			provider: "fournisseur",
			tool: "outil",
			calls: "appels",
			time: "temps",
			cacheShort: "cache",
			cacheRead: "lecture",
			cacheWrite: "écriture",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "nouvelle",
			reasoning: "raisonnement",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Facturé au coût : {cost} × {rate} = {credits} cr",
			chargeByTier: "Prix fixe du niveau {tier} : {credits} cr",
			chargeTools: "+ {n} cr en outils",
			chargeNone: "Rien facturé",
			areaAttendant: "assistant",
			areaBen: "Ben",
			loopNote:
				"{calls} appels dans ce tour : chacun renvoie toute la conversation, c'est ainsi que l'API fonctionne. {pct}% venait du cache.",
			groupedIn: "Jours découpés en {zone}.",
		},
		history: {
			title: "Historique",
			lead: "Appel par appel, sur tous les espaces de travail — les filtres du panneau coupent cette liste aussi.",
			searchLabel: "Rechercher dans l'historique",
			searchPlaceholder: "rechercher par texte, agent ou espace de travail",
			inFilter: "{n} appel dans cette coupe | {n} appels dans cette coupe",
			failed: "a échoué",
			noModel: "sans modèle",
			empty: "Aucun appel sur la période avec ces filtres.",
			previous: "précédent",
			next: "suivant",
			pageOf: "page {page} sur {total}",
			asked: "ce qui a été demandé",
			answered: "ce qui a été répondu",
			tools: "outils",
			noTools: "aucun outil n'a été appelé",
			toolIn: "entrée",
			toolOut: "sortie",
			error: "erreur",
			opening: "ouverture de l'appel…",
			openFailed: "Impossible d'ouvrir cet appel.",
			ratingNote: "note",
			ratedDown: "signalés faux",
			ratedUp: "signalés justes",
			calls: "appels au modèle",
			noCalls:
				"Aucun appel enregistré pour ce tour — il a eu lieu avant que le journal par appel existe.",
			callFallback: "maillon {n}",
			systemPrompt: "prompt système",
			units: "unités",
			conversation: "Conversation",
			objective: "objectif",
			missionPreamble: "instructions fixes de la mission",
			noReply: "Pas de réponse texte : le travail est passé par {n} outils.",
			noReplyShort: "pas de réponse texte · {n} outils",
			interrupted: "interrompu",
			seeTools: "Voir les outils",
			previousTurn: "Tour précédent",
			nextTurn: "Tour suivant",
			close: "Fermer",
			source: {
				panel: "panneau",
				channel: "canal",
				mission: "mission",
				automation: "automatisation",
			},
		},
		errors: {
			title: "Erreurs",
			scopeOpen: "ouvertes",
			scopeAll: "tout",
			lead: "Une ligne par défaillance distincte, avec le nombre de fois où elle est arrivée. Durable — un redémarrage ne l'efface pas.",
			emptyOpen: "Rien d'ouvert. C'est ce qu'on espère d'un écran d'erreurs.",
			emptyAll: "Aucune erreur enregistrée.",
			markFailed: "Impossible de marquer cette erreur.",
			reopen: "rouvrir",
			resolve: "résoudre",
			last: "dernière {ago}",
			first: "première {ago}",
			cameBack: "revenue après avoir été résolue",
			hideStack: "masquer la pile",
			showStack: "voir la pile",
			now: "maintenant",
			minutesAgo: "il y a {n} min",
			hoursAgo: "il y a {n} h",
			daysAgo: "il y a {n} j",
		},
		tenants: {
			title: "Espaces de travail",
			searchPlaceholder: "rechercher par nom ou slug",
			searchLabel: "Rechercher un espace de travail",
			filterAll: "tous",
			filterActive: "actifs",
			filterSuspended: "suspendus",
			filterArchived: "archivés",
			total: "{n} au total",
			workspace: "espace de travail",
			country: "pays",
			people: "personnes",
			agents: "agents",
			turns30d: "échanges 30 j",
			cost30d: "coût 30 j",
			credits: "crédits",
			creditsBlocked: "bloqués",
			sites: "Pages",
			plan: "plan",
			channels: "channels",
			messages30d: "msgs 30d",
			kindAll: "all types",
			kinds: {
				trial: "trial",
				courtesy: "courtesy",
				paid: "paying",
				free: "free",
				ended: "ended",
				none: "no plan",
			},
			trialUntil: "trial until {date}",
			peek: "Open as the owner in a new tab",
			peekAs: "Open as {name} in a new tab",
			peekFailed: "Could not open the account.",
			sortBy: "Sort by {column}",
			detail: {
				loading: "Loading…",
				channels: "Channels",
				noChannels: "No channel.",
				connectedAt: "connected {when}",
				people: "People",
				structure: "Structure",
				teams: "Teams",
				week: "Last 7 days",
				openConversations: "open conversations",
				agentTurns: "agent turns",
				automationRuns: "automation runs",
				errors: "errors",
				lastMessage: "last message",
				openFull: "Open full record",
				builtKinds: {
					entity: "Records",
					agent: "Agents",
					pipeline: "Funnels",
					automation: "Automations",
					artifact: "Artifacts",
				},
			},
			sitesEmpty: "Aucune page créée.",
			siteDraft: "brouillon",
			siteDefault: "principale",
			siteNoAddress: "pas encore d’adresse",
			activity: "activité",
			state: "état",
			never: "jamais",
			now: "maintenant",
			reactivate: "réactiver",
			suspend: "suspendre",
			archive: "archiver",
			actionReactivate: "Réactiver",
			actionSuspend: "Suspendre",
			actionArchive: "Archiver",
			purge: "supprimer définitivement",
			actionPurge: "Supprimer définitivement",
			consequenceArchive:
				"Plus personne n'y entre et l'espace de travail quitte la liste des actifs.",
			consequenceSuspend: "Personne n'y entre et rien n'en sort tant qu'il est suspendu.",
			consequencePurge:
				"Supprime cet espace de travail et tout ce qu'il contient — personnes, conversations, contacts, fichiers, historique. Il n'y a pas de retour.",
			typeToConfirm: "Tapez {slug} pour confirmer.",
			typeLabel: "Tapez {slug} pour confirmer",
			empty: "Aucun espace de travail ne correspond à ce filtre.",
			actionFailed: "Impossible de changer l'état de cet espace de travail.",
			purgeFailed:
				"Impossible de supprimer cet espace de travail. Il reste archivé — rien n'a été touché.",
			bulkSelected: "{n} sélectionné | {n} sélectionnés",
			bulkPurge: "supprimer tous les sélectionnés",
			bulkConsequence:
				"Ceci archive et supprime POUR DE BON les espaces listés — données, fichiers et tables. Sans retour.",
			bulkType: "Tapez {word} pour confirmer.",
			bulkWord: "SUPPRIMER",
			bulkFailed: "Impossible de supprimer : {slugs}. Les autres ont été supprimés.",
			bulkDone: "Tous les espaces sélectionnés ont été supprimés.",
		},
	},
	it: {
		credits: {
			title: "Crediti a mano",
			balance: "piano {plan} · bonus {bonus}",
			blocked: "L'IA è bloccata in questo spazio di lavoro",
			amount: "Crediti",
			amountHint: "Un valore negativo toglie.",
			pool: "Tasca",
			poolBonus: "Bonus (sopravvive al ciclo)",
			poolPlan: "Piano (si azzera alla chiusura)",
			reason: "Perché — almeno dieci caratteri",
			apply: "Applica",
			applied: "{n} crediti applicati. Il saldo ora è {total}.",
			reasonShort:
				"Il motivo ha bisogno di almeno dieci caratteri — è quello che spiegherà questa riga fra mesi.",
			blockTitle: "Stop manuale",
			blockLead:
				"Sopravvive a ogni ricarica: aggiungere crediti non lo annulla. Solo quello che c'è qui lo toglie.",
			blockReason: "Perché bloccare (o togliere il blocco) — almeno dieci caratteri",
			block: "Blocca l'IA",
			unblock: "Togli il blocco",
			blockedSince: "Bloccato: {reason}",
			historyTitle: "Che cosa è stato mosso qui",
			historyEmpty: "Nessuno ha ancora toccato questo saldo a mano.",
			historyNone: "nessun motivo registrato",
			kindAdjustment: "rettifica",
			kindBlock: "blocco",
			kindUnblock: "sblocco",
		},
		audit: {
			title: "Audit",
			lead: "Ogni azione di piattaforma, salvata in tabella — sopravvive a un riavvio.",
			empty: "Su questa installazione non è ancora stato fatto niente.",
			someone: "qualcuno",
			aWorkspace: "uno spazio di lavoro",
			statusChange: "{who} ha spostato {what} da {from} a {to}",
			grant: "{who} ha aperto una sessione di supporto su {what} per {minutes} min",
			revoke: "{who} ha revocato la sessione di supporto su {what}",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Presenza",
			readyOf: "{ready} su {total} pronti",
			ready: "pronto",
			openConversations: "{n} aperte",
		},
		notice: {
			title: "Avvisi",
			new: "nuovo avviso",
			message: "Messaggio",
			target: "destinazione",
			allWorkspaces: "tutti gli spazi",
			duration: "per",
			tone: "colore",
			for1h: "1 ora",
			for1d: "1 giorno",
			for1w: "1 settimana",
			for30d: "30 giorni",
			publish: "pubblica",
			link: "Link (facoltativo)",
			open: "apri",
			empty: "Nessun avviso attivo.",
			createFailed: "Impossibile pubblicare l’avviso.",
			revokeFailed: "Impossibile revocare l’avviso.",
			bannerUntil: "fino a {time}",
		},
		support: {
			bannerUntil:
				"Il supporto della piattaforma può vedere questo spazio di lavoro fino alle {time}.",
			bannerReason: "Motivo: “{reason}”",
			bannerGrantedBy: " — concesso da {name}",
			openTitle: "Sessioni di supporto aperte",
			endsIn: "“{reason}” — {who}, finisce tra {remaining}",
			expiring: "in scadenza",
			revoke: "Revoca",
			revokeFailed: "Non è stato possibile revocare quella sessione.",
			copyLink: "copia link di accesso",
			linkCopied: "Link copiato — chi lo possiede entra come admin fino alla scadenza.",
			grantFailed: "Non è stato possibile aprire la sessione di supporto.",
			activeUntil: "Sessione di supporto aperta fino alle {until} — “{reason}”.",
			bandNote: "Questo spazio di lavoro sta vedendo una fascia al riguardo.",
			reason: "Motivo — lo spazio di lavoro leggerà questa frase",
			reasonPlaceholder: "perché qualcuno deve guardare questo spazio di lavoro",
			forHowLong: "Per",
			open: "Apri la sessione",
		},
		usage: {
			tabs: {
				label: "Viste del consumo",
				history: "Cronologia",
				panel: "Pannello",
				workspaces: "Per spazio",
			},
			title: "Consumo",
			period: "Periodo",
			days: "{n} giorni",
			inPeriod: "{money} nel periodo",
			workspace: "spazio di lavoro",
			day: "giorno",
			model: "modello",
			turns: "turni",
			errors: "errori",
			tokens: "token",
			cost: "costo",
			models: "modelli",
			empty:
				"Nessun turno nel periodo. Se sembra sbagliato, è una domanda per il registro attività — la lettura gira dentro il contesto di ogni spazio di lavoro, mai fuori da esso.",
			from: "da",
			to: "a",
			allWorkspaces: "tutti gli spazi di lavoro",
			workspaceLabel: "Spazio di lavoro",
			agentLabel: "Agente",
			allAgents: "tutti gli agenti",
			sourceLabel: "Origine",
			allSources: "tutte le origini",
			modelLabel: "Modello",
			allModels: "tutti i modelli",
			providerLabel: "Fornitore",
			allProviders: "tutti i fornitori",
			onlyErrors: "solo errori",
			clear: "azzera i filtri",
			kpiCalls: "chiamate",
			kpiTokens: "token in entrata e in uscita",
			kpiCacheRate: "entrata dalla cache",
			kpiTurns: "{n} turno | {n} turni",
			kindLabel: "tipo di chiamata",
			allKinds: "tutti i tipi",
			kind: {
				chat: "conversazione",
				embedding: "embedding",
				image: "immagine",
				speech: "voce",
				transcription: "trascrizione",
			},
			kpiCost: "costo",
			kpiCredits: "crediti addebitati",
			kpiAnswers: "{n} risposta addebitata | {n} risposte addebitate",
			kpiLatency: "latenza media",
			kpiErrors: "errori",
			creditsNote:
				"I crediti contano l'intera finestra di ogni spazio di lavoro: i tagli per agente, modello ed errore non arrivano al mastro.",
			chartTitle: "costo per giorno",
			chartBar: "{day} · {money} · {n} turni",
			byWorkspace: "per spazio di lavoro",
			byAgent: "per agente",
			byModel: "per modello",
			byTool: "per strumento — dov'è finito il tempo",
			byToolHint:
				"chiamate, tempo ed errori; una chiamata di strumento non ha un prezzo da stampare",
			credits: "crediti",
			latency: "latenza",
			agent: "agente",
			provider: "fornitore",
			tool: "strumento",
			calls: "chiamate",
			time: "tempo",
			cacheShort: "cache",
			cacheRead: "lettura",
			cacheWrite: "scrittura",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "nuova",
			reasoning: "ragionamento",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Addebitato a costo: {cost} × {rate} = {credits} cr",
			chargeByTier: "Prezzo fisso del livello {tier}: {credits} cr",
			chargeTools: "+ {n} cr in strumenti",
			chargeNone: "Nessun addebito",
			areaAttendant: "assistente",
			areaBen: "Ben",
			loopNote:
				"{calls} chiamate in questo turno: ognuna rinvia l'intera conversazione, è così che funziona l'API. Il {pct}% è arrivato dalla cache.",
			groupedIn: "Giorni tagliati in {zone}.",
		},
		history: {
			title: "Cronologia",
			lead: "Chiamata per chiamata, su tutti gli spazi di lavoro — i filtri del pannello tagliano anche questa lista.",
			searchLabel: "Cerca nella cronologia",
			searchPlaceholder: "cerca per testo, agente o spazio di lavoro",
			inFilter: "{n} chiamata in questo taglio | {n} chiamate in questo taglio",
			failed: "fallita",
			noModel: "senza modello",
			empty: "Nessuna chiamata nel periodo con questi filtri.",
			previous: "precedente",
			next: "successiva",
			pageOf: "pagina {page} di {total}",
			asked: "che cosa è stato chiesto",
			answered: "che cosa è stato risposto",
			tools: "strumenti",
			noTools: "non è stato chiamato nessuno strumento",
			toolIn: "entrata",
			toolOut: "uscita",
			error: "errore",
			opening: "apertura della chiamata…",
			openFailed: "Non è stato possibile aprire questa chiamata.",
			ratingNote: "nota",
			ratedDown: "segnalati errati",
			ratedUp: "segnalati corretti",
			calls: "chiamate al modello",
			noCalls:
				"Nessuna chiamata registrata in questo turno — è avvenuto prima che esistesse il registro per chiamata.",
			callFallback: "anello {n}",
			systemPrompt: "prompt di sistema",
			units: "unità",
			conversation: "Conversazione",
			objective: "obiettivo",
			missionPreamble: "istruzioni fisse della missione",
			noReply: "Nessuna risposta di testo: il lavoro è passato da {n} strumenti.",
			noReplyShort: "nessuna risposta di testo · {n} strumenti",
			interrupted: "interrotto",
			seeTools: "Vedi gli strumenti",
			previousTurn: "Turno precedente",
			nextTurn: "Turno successivo",
			close: "Chiudi",
			source: {
				panel: "pannello",
				channel: "canale",
				mission: "missione",
				automation: "automazione",
			},
		},
		errors: {
			title: "Errori",
			scopeOpen: "aperti",
			scopeAll: "tutto",
			lead: "Una riga per ogni guasto distinto, con quante volte è successo. Duraturo — un riavvio non lo cancella.",
			emptyOpen: "Niente di aperto. Che è quello che si spera da una schermata di errori.",
			emptyAll: "Nessun errore registrato.",
			markFailed: "Non è stato possibile segnare quell'errore.",
			reopen: "riapri",
			resolve: "risolvi",
			last: "ultima {ago}",
			first: "prima {ago}",
			cameBack: "è tornato dopo essere stato risolto",
			hideStack: "nascondi lo stack",
			showStack: "mostra lo stack",
			now: "adesso",
			minutesAgo: "{n} min fa",
			hoursAgo: "{n} h fa",
			daysAgo: "{n} g fa",
		},
		tenants: {
			title: "Spazi di lavoro",
			searchPlaceholder: "cerca per nome o slug",
			searchLabel: "Cerca spazio di lavoro",
			filterAll: "tutti",
			filterActive: "attivi",
			filterSuspended: "sospesi",
			filterArchived: "archiviati",
			total: "{n} in totale",
			workspace: "spazio di lavoro",
			country: "paese",
			people: "persone",
			agents: "agenti",
			turns30d: "turni 30gg",
			cost30d: "costo 30gg",
			credits: "crediti",
			creditsBlocked: "bloccati",
			sites: "Pagine",
			plan: "plan",
			channels: "channels",
			messages30d: "msgs 30d",
			kindAll: "all types",
			kinds: {
				trial: "trial",
				courtesy: "courtesy",
				paid: "paying",
				free: "free",
				ended: "ended",
				none: "no plan",
			},
			trialUntil: "trial until {date}",
			peek: "Open as the owner in a new tab",
			peekAs: "Open as {name} in a new tab",
			peekFailed: "Could not open the account.",
			sortBy: "Sort by {column}",
			detail: {
				loading: "Loading…",
				channels: "Channels",
				noChannels: "No channel.",
				connectedAt: "connected {when}",
				people: "People",
				structure: "Structure",
				teams: "Teams",
				week: "Last 7 days",
				openConversations: "open conversations",
				agentTurns: "agent turns",
				automationRuns: "automation runs",
				errors: "errors",
				lastMessage: "last message",
				openFull: "Open full record",
				builtKinds: {
					entity: "Records",
					agent: "Agents",
					pipeline: "Funnels",
					automation: "Automations",
					artifact: "Artifacts",
				},
			},
			sitesEmpty: "Nessuna pagina creata.",
			siteDraft: "bozza",
			siteDefault: "principale",
			siteNoAddress: "ancora senza indirizzo",
			activity: "attività",
			state: "stato",
			never: "mai",
			now: "adesso",
			reactivate: "riattiva",
			suspend: "sospendi",
			archive: "archivia",
			actionReactivate: "Riattiva",
			actionSuspend: "Sospendi",
			actionArchive: "Archivia",
			purge: "elimina per sempre",
			actionPurge: "Elimina per sempre",
			consequenceArchive:
				"Non entra più nessuno e lo spazio di lavoro esce dalla lista degli attivi.",
			consequenceSuspend: "Nessuno entra e niente ne esce finché è sospeso.",
			consequencePurge:
				"Elimina questo spazio di lavoro e tutto quello che contiene — persone, conversazioni, contatti, file, cronologia. Non si torna indietro.",
			typeToConfirm: "Scrivi {slug} per confermare.",
			typeLabel: "Scrivi {slug} per confermare",
			empty: "Nessuno spazio di lavoro con questo filtro.",
			actionFailed: "Non è stato possibile cambiare lo stato di quello spazio di lavoro.",
			purgeFailed:
				"Non è stato possibile eliminare quello spazio di lavoro. Resta archiviato — non è stato toccato niente.",
			bulkSelected: "{n} selezionato | {n} selezionati",
			bulkPurge: "elimina tutti i selezionati",
			bulkConsequence:
				"Questo archivia ed elimina PER SEMPRE gli spazi elencati — dati, file e tabelle. Senza ritorno.",
			bulkType: "Scrivi {word} per confermare.",
			bulkWord: "ELIMINA",
			bulkFailed: "Impossibile eliminare: {slugs}. Gli altri sono stati eliminati.",
			bulkDone: "Tutti gli spazi selezionati sono stati eliminati.",
		},
	},
	de: {
		credits: {
			title: "Credits von Hand",
			balance: "Tarif {plan} · Bonus {bonus}",
			blocked: "Die KI ist für diesen Workspace gesperrt",
			amount: "Credits",
			amountHint: "Negativ nimmt zurück.",
			pool: "Topf",
			poolBonus: "Bonus (überlebt den Zyklus)",
			poolPlan: "Tarif (wird zum Abschluss zurückgesetzt)",
			reason: "Warum — mindestens zehn Zeichen",
			apply: "Anwenden",
			applied: "{n} Credits gebucht. Das Guthaben ist jetzt {total}.",
			reasonShort:
				"Der Grund braucht mindestens zehn Zeichen — er ist es, der diese Zeile in ein paar Monaten erklärt.",
			blockTitle: "Manueller Stopp",
			blockLead:
				"Er überlebt jede Aufladung: Credits nachzufüllen hebt ihn nicht auf. Nur was hier steht, hebt ihn auf.",
			blockReason: "Warum sperren (oder aufheben) — mindestens zehn Zeichen",
			block: "KI sperren",
			unblock: "Sperre aufheben",
			blockedSince: "Gesperrt: {reason}",
			historyTitle: "Was hier bewegt wurde",
			historyEmpty: "Dieses Guthaben hat noch niemand von Hand bewegt.",
			historyNone: "kein Grund erfasst",
			kindAdjustment: "Korrektur",
			kindBlock: "Sperre",
			kindUnblock: "Entsperrung",
		},
		audit: {
			title: "Audit",
			lead: "Jede Plattform-Aktion, in einer Tabelle gespeichert — sie überlebt einen Neustart.",
			empty: "Auf dieser Installation wurde noch nichts gemacht.",
			someone: "jemand",
			aWorkspace: "ein Workspace",
			statusChange: "{who} hat {what} von {from} auf {to} gesetzt",
			grant: "{who} hat eine Support-Sitzung auf {what} für {minutes} Min geöffnet",
			revoke: "{who} hat die Support-Sitzung auf {what} widerrufen",
			other: "{who} · {action} · {what}",
		},
		presence: {
			title: "Präsenz",
			readyOf: "{ready} von {total} bereit",
			ready: "bereit",
			openConversations: "{n} offen",
		},
		notice: {
			title: "Hinweise",
			new: "neuer Hinweis",
			message: "Nachricht",
			target: "Ziel",
			allWorkspaces: "alle Workspaces",
			duration: "für",
			tone: "Farbe",
			for1h: "1 Stunde",
			for1d: "1 Tag",
			for1w: "1 Woche",
			for30d: "30 Tage",
			publish: "veröffentlichen",
			link: "Link (optional)",
			open: "öffnen",
			empty: "Keine aktiven Hinweise.",
			createFailed: "Hinweis konnte nicht veröffentlicht werden.",
			revokeFailed: "Hinweis konnte nicht widerrufen werden.",
			bannerUntil: "bis {time}",
		},
		support: {
			bannerUntil: "Der Plattform-Support kann diesen Workspace bis {time} sehen.",
			bannerReason: "Grund: „{reason}“",
			bannerGrantedBy: " — gewährt von {name}",
			openTitle: "Offene Support-Sitzungen",
			endsIn: "„{reason}“ — {who}, endet in {remaining}",
			expiring: "läuft ab",
			revoke: "Widerrufen",
			revokeFailed: "Diese Sitzung konnte nicht widerrufen werden.",
			copyLink: "Zugangslink kopieren",
			linkCopied: "Link kopiert — wer ihn hat, ist bis zur Frist Admin.",
			grantFailed: "Die Support-Sitzung konnte nicht geöffnet werden.",
			activeUntil: "Support-Sitzung offen bis {until} — „{reason}“.",
			bandNote: "Dieser Workspace sieht dazu ein Band.",
			reason: "Grund — der Workspace liest diesen Satz",
			reasonPlaceholder: "warum jemand in diesen Workspace schauen muss",
			forHowLong: "Für",
			open: "Sitzung öffnen",
		},
		usage: {
			tabs: {
				label: "Verbrauchsansichten",
				history: "Verlauf",
				panel: "Übersicht",
				workspaces: "Nach Arbeitsbereich",
			},
			title: "Verbrauch",
			period: "Zeitraum",
			days: "{n} Tage",
			inPeriod: "{money} im Zeitraum",
			workspace: "Workspace",
			day: "Tag",
			model: "Modell",
			turns: "Runden",
			errors: "Fehler",
			tokens: "Tokens",
			cost: "Kosten",
			models: "Modelle",
			empty:
				"Keine Runde im Zeitraum. Wenn das falsch aussieht, ist es eine Frage ans Protokoll — die Abfrage läuft im eigenen Kontext jedes Workspace, nie außerhalb davon.",
			from: "von",
			to: "bis",
			allWorkspaces: "alle Workspaces",
			workspaceLabel: "Workspace",
			agentLabel: "Agent",
			allAgents: "alle Agenten",
			sourceLabel: "Herkunft",
			allSources: "alle Herkünfte",
			modelLabel: "Modell",
			allModels: "alle Modelle",
			providerLabel: "Anbieter",
			allProviders: "alle Anbieter",
			onlyErrors: "nur Fehler",
			clear: "Filter löschen",
			kpiCalls: "Aufrufe",
			kpiTokens: "Tokens rein und raus",
			kpiCacheRate: "Eingabe aus dem Cache",
			kpiTurns: "{n} Zug | {n} Züge",
			kindLabel: "Aufruftyp",
			allKinds: "alle Typen",
			kind: {
				chat: "Konversation",
				embedding: "Embedding",
				image: "Bild",
				speech: "Sprache",
				transcription: "Transkription",
			},
			kpiCost: "Kosten",
			kpiCredits: "berechnete Credits",
			kpiAnswers: "{n} Antwort berechnet | {n} Antworten berechnet",
			kpiLatency: "durchschnittliche Latenz",
			kpiErrors: "Fehler",
			creditsNote:
				"Credits zählen das ganze Fenster jedes Workspace: die Schnitte nach Agent, Modell und Fehler reichen nicht bis ins Hauptbuch.",
			chartTitle: "Kosten pro Tag",
			chartBar: "{day} · {money} · {n} Runden",
			byWorkspace: "nach Workspace",
			byAgent: "nach Agent",
			byModel: "nach Modell",
			byTool: "nach Werkzeug — wohin die Zeit ging",
			byToolHint: "Aufrufe, Zeit und Fehler; ein Werkzeugaufruf hat keinen Preis zum Ausweisen",
			credits: "Credits",
			latency: "Latenz",
			agent: "Agent",
			provider: "Anbieter",
			tool: "Werkzeug",
			calls: "Aufrufe",
			time: "Zeit",
			cacheShort: "Cache",
			cacheRead: "Lesen",
			cacheWrite: "Schreiben",
			/** A entrada que NÃO veio do cache — o que a repetição do laço não cobre. */
			fresh: "neu",
			reasoning: "Denken",
			/**
			 * A frase que desfaz a leitura errada de "o prompt de sistema rodou N vezes".
			 * Rodou mesmo, e é assim que tem de ser; o que a torna barata é o cache, e
			 * sem dizer isso o número de entrada parece um desperdício absurdo.
			 */
			/** Como o turno foi cobrado — a linha do extrato, dita (0280). */
			chargeByCost: "Nach Kosten berechnet: {cost} × {rate} = {credits} cr",
			chargeByTier: "Festpreis Stufe {tier}: {credits} cr",
			chargeTools: "+ {n} cr für Werkzeuge",
			chargeNone: "Nichts berechnet",
			areaAttendant: "Assistent",
			areaBen: "Ben",
			loopNote:
				"{calls} Aufrufe in diesem Turn: jeder sendet das ganze Gespräch erneut, so arbeitet die API. {pct}% kamen aus dem Cache.",
			groupedIn: "Tage geschnitten in {zone}.",
		},
		history: {
			title: "Verlauf",
			lead: "Aufruf für Aufruf, über alle Workspaces — die Filter des Panels schneiden auch diese Liste.",
			searchLabel: "Im Verlauf suchen",
			searchPlaceholder: "nach Text, Agent oder Workspace suchen",
			inFilter: "{n} Aufruf in diesem Schnitt | {n} Aufrufe in diesem Schnitt",
			failed: "fehlgeschlagen",
			noModel: "kein Modell",
			empty: "Kein Aufruf im Zeitraum mit diesen Filtern.",
			previous: "zurück",
			next: "weiter",
			pageOf: "Seite {page} von {total}",
			asked: "was gefragt wurde",
			answered: "was geantwortet wurde",
			tools: "Werkzeuge",
			noTools: "es wurde kein Werkzeug aufgerufen",
			toolIn: "Eingabe",
			toolOut: "Ausgabe",
			error: "Fehler",
			opening: "Aufruf wird geöffnet…",
			openFailed: "Dieser Aufruf konnte nicht geöffnet werden.",
			ratingNote: "Notiz",
			ratedDown: "als falsch markiert",
			ratedUp: "als richtig markiert",
			calls: "Aufrufe an das Modell",
			noCalls:
				"Kein Aufruf für diesen Zug erfasst — er lief, bevor das Protokoll pro Aufruf existierte.",
			callFallback: "Glied {n}",
			systemPrompt: "System-Prompt",
			units: "Einheiten",
			conversation: "Gespräch",
			objective: "Ziel",
			missionPreamble: "feste Anweisungen der Mission",
			noReply: "Keine Textantwort: die Arbeit lief über {n} Werkzeuge.",
			noReplyShort: "keine Textantwort · {n} Werkzeuge",
			interrupted: "unterbrochen",
			seeTools: "Werkzeuge ansehen",
			previousTurn: "Vorheriger Turn",
			nextTurn: "Nächster Turn",
			close: "Schließen",
			source: {
				panel: "Panel",
				channel: "Kanal",
				mission: "Mission",
				automation: "Automatisierung",
			},
		},
		errors: {
			title: "Fehler",
			scopeOpen: "offen",
			scopeAll: "alles",
			lead: "Eine Zeile pro eigenständigem Fehler, mit der Anzahl der Vorfälle. Dauerhaft — ein Neustart löscht sie nicht.",
			emptyOpen: "Nichts offen. Was man sich von einer Fehlermaske erhofft.",
			emptyAll: "Kein Fehler erfasst.",
			markFailed: "Dieser Fehler konnte nicht markiert werden.",
			reopen: "wieder öffnen",
			resolve: "erledigen",
			last: "zuletzt {ago}",
			first: "zuerst {ago}",
			cameBack: "kam zurück, nachdem er erledigt war",
			hideStack: "Stack ausblenden",
			showStack: "Stack anzeigen",
			now: "jetzt",
			minutesAgo: "vor {n} Min",
			hoursAgo: "vor {n} Std",
			daysAgo: "vor {n} T",
		},
		tenants: {
			title: "Workspaces",
			searchPlaceholder: "nach Name oder Slug suchen",
			searchLabel: "Workspace suchen",
			filterAll: "alle",
			filterActive: "aktiv",
			filterSuspended: "gesperrt",
			filterArchived: "archiviert",
			total: "{n} insgesamt",
			workspace: "Workspace",
			country: "Land",
			people: "Personen",
			agents: "Agenten",
			turns30d: "Runden 30 T",
			cost30d: "Kosten 30 T",
			credits: "Credits",
			creditsBlocked: "gesperrt",
			sites: "Seiten",
			plan: "plan",
			channels: "channels",
			messages30d: "msgs 30d",
			kindAll: "all types",
			kinds: {
				trial: "trial",
				courtesy: "courtesy",
				paid: "paying",
				free: "free",
				ended: "ended",
				none: "no plan",
			},
			trialUntil: "trial until {date}",
			peek: "Open as the owner in a new tab",
			peekAs: "Open as {name} in a new tab",
			peekFailed: "Could not open the account.",
			sortBy: "Sort by {column}",
			detail: {
				loading: "Loading…",
				channels: "Channels",
				noChannels: "No channel.",
				connectedAt: "connected {when}",
				people: "People",
				structure: "Structure",
				teams: "Teams",
				week: "Last 7 days",
				openConversations: "open conversations",
				agentTurns: "agent turns",
				automationRuns: "automation runs",
				errors: "errors",
				lastMessage: "last message",
				openFull: "Open full record",
				builtKinds: {
					entity: "Records",
					agent: "Agents",
					pipeline: "Funnels",
					automation: "Automations",
					artifact: "Artifacts",
				},
			},
			sitesEmpty: "Keine Seite erstellt.",
			siteDraft: "Entwurf",
			siteDefault: "Haupt",
			siteNoAddress: "noch keine Adresse",
			activity: "Aktivität",
			state: "Status",
			never: "nie",
			now: "jetzt",
			reactivate: "reaktivieren",
			suspend: "sperren",
			archive: "archivieren",
			actionReactivate: "Reaktivieren",
			actionSuspend: "Sperren",
			actionArchive: "Archivieren",
			purge: "endgültig löschen",
			actionPurge: "Endgültig löschen",
			consequenceArchive:
				"Niemand kommt mehr hinein und der Workspace verlässt die Liste der aktiven.",
			consequenceSuspend: "Niemand kommt hinein und nichts geht hinaus, solange er gesperrt ist.",
			consequencePurge:
				"Löscht diesen Workspace und alles darin — Personen, Konversationen, Kontakte, Dateien, Verlauf. Es gibt keinen Weg zurück.",
			typeToConfirm: "Tippe {slug} zur Bestätigung.",
			typeLabel: "Tippe {slug} zur Bestätigung",
			empty: "Kein Workspace passt zu diesem Filter.",
			actionFailed: "Der Status dieses Workspace konnte nicht geändert werden.",
			purgeFailed:
				"Dieser Workspace konnte nicht gelöscht werden. Er bleibt archiviert — nichts wurde angefasst.",
			bulkSelected: "{n} ausgewählt",
			bulkPurge: "alle ausgewählten löschen",
			bulkConsequence:
				"Das archiviert und löscht die gelisteten Workspaces ENDGÜLTIG — Daten, Dateien und Tabellen. Ohne Rückweg.",
			bulkType: "Tippe {word} zur Bestätigung.",
			bulkWord: "LOESCHEN",
			bulkFailed: "Nicht gelöscht: {slugs}. Die übrigen wurden gelöscht.",
			bulkDone: "Alle ausgewählten Workspaces wurden gelöscht.",
		},
	},
} as const;
